import React from 'react';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { get } from 'lodash';
import { UserAttributes } from '../../models';
import ResourceTable, { IColumn } from '../../components/ResourceTable';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import { UserDto } from 'models/dtoTypes';
import { GetUsers, CreateUser, DisableUser, EnableUser } from 'services/user';
import CreateResourceModal from '../../dialogs/CreateResourceModal';
import generateNonce from 'utils/generateNonce';
import Notice from '../../components/Notice';
import Link from 'components/Link';
import Button from '@mui/material/Button';
import { userFilterSchema, userCreateSchema } from './_userSchema';
import { useTranslation } from 'react-i18next';

const PREFIX = 'AdminUsersPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function AdminUsersPage(): JSX.Element {
  const [openCreate, setOpenCreate] = React.useState(false);
  const [nonce, setNonce] = React.useState('');
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const { t } = useTranslation('default');

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      console.log(e.response?.data);
      const errorMessage = get(e.response?.data, 'message') || t('something-went-wrong');
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const handleCreate = async (attrs: UserAttributes) => {
    try {
      const createdMessage = await CreateUser({
        ...attrs,
      });
      setNonce(generateNonce);
      setOpenCreate(false);
      setNoticeData({
        message: createdMessage || t('successfully-created'),
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  const handleDisableUser = async (user: UserDto) => {
    try {
      return await DisableUser(user.id);
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: t('cannot-disable-this-user'),
        severity: 'error',
      });
    }
  };

  const handleEnableUser = async (user: UserDto) => {
    try {
      return await EnableUser(user.id);
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: t('cannot-enable-this-user'),
        severity: 'error',
      });
    }
  };

  const generateColumns = (): IColumn<UserDto>[] => [
    {
      label: t('email'),
      dataIndex: 'email',
      sortIndex: 'email',
      render: (model: UserDto) => {
        return <Link to={`/users/${model.id}`} text={model.email || '(No Email)'} />;
      },
    },
    {
      label: t('first-name'),
      dataIndex: 'firstName',
    },
    {
      label: t('last-name'),
      dataIndex: 'lastName',
    },
    {
      label: t('role'),
      dataIndex: 'userRole',
      sortIndex: 'userRole',
    },
    {
      label: t('organisation'),
      render: (user: UserDto) => {
        const orgName = user?.organisationName as string;
        const orgId = user?.organisationId as string;
        if (orgName) {
          return <Link to={`/organisations/${orgId || ''}`} text={orgName} />;
        } else {
          return <></>;
        }
      },
    },
    {
      label: t('account-enabled'),
      sortIndex: 'accountEnabled',
      render: (user) => {
        const enabled = Boolean(user.accountEnabled);
        return <Chip label={enabled ? 'Yes' : 'No'} color={enabled ? 'primary' : 'default'} />;
      },
    },
    {
      label: t('action'),
      actions: [
        {
          label: t('enable'),
          hide: (user) => !user.aadUserId || user.accountEnabled,
          onClick: handleEnableUser,
        },
        {
          label: t('disable'),
          hide: (user) => !user.aadUserId || !user.accountEnabled,
          onClick: handleDisableUser,
        },
      ],
    },
  ];

  return (
    <Root>
      <div className={classes.pageHeader}>
        <h2>{t('users')}</h2>
        <Button onClick={() => setOpenCreate(true)} variant="contained" startIcon={<AddIcon />}>
          {t('create')}
        </Button>
      </div>
      <ResourceTable
        key={nonce}
        filterSchema={userFilterSchema(t)}
        columns={generateColumns()}
        onFetch={GetUsers}
        defaultSort={{
          sortColumn: 'email',
          isDesc: false,
        }}
      />
      <CreateResourceModal<UserAttributes>
        open={openCreate}
        label={t('create-a-new-user')}
        schema={userCreateSchema(t)}
        onClose={() => setOpenCreate(false)}
        onSave={handleCreate}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
